import React, { useMemo, useState } from 'react';

import { AssetAllocation, AssetAllocationCategory, AssetAllocationSource } from '@/cip/_openapi/portfolio-optimizer';
import { ASSET_CLASS_BREAKDOWN_CATEGORIES, formatRatio } from 'utils/asset-allocation-utils';

import { ManageOverridesDialog } from './ManageOverridesDialog';

interface CategoryRow {
    category: AssetAllocationCategory;
    morningstar: number | null;
    override: number | null;
    value: number | null;
}

interface FundAllocationCardProps {
    allocation: AssetAllocation;
}

const buildRows = (allocation: AssetAllocation): { rows: CategoryRow[]; uncategorized: number | null } => {
    const morningstarByCategory = new Map<string, number>();
    const overrideByCategory = new Map<string, number>();
    let uncategorized: number | null = null;

    for (const item of allocation.items ?? []) {
        if (item.category === AssetAllocationCategory.UNCATEGORIZED) {
            uncategorized = item.value ?? null;
            continue;
        }
        if (item.source === AssetAllocationSource.MANUAL) {
            overrideByCategory.set(item.category, item.value ?? 0);
        } else if (item.source === AssetAllocationSource.MORNINGSTAR) {
            morningstarByCategory.set(item.category, item.value ?? 0);
        }
    }

    const hasOverrides = overrideByCategory.size > 0;

    const rows = ASSET_CLASS_BREAKDOWN_CATEGORIES.map((category) => {
        const morningstar = morningstarByCategory.get(category) ?? null;
        const override = overrideByCategory.get(category) ?? null;
        const value = hasOverrides ? override : morningstar;
        return { category, morningstar, override, value };
    });

    return { rows, uncategorized };
};

export const FundAllocationCard: React.FC<FundAllocationCardProps> = ({ allocation }) => {
    const [manageOpen, setManageOpen] = useState(false);

    const { rows, uncategorized } = useMemo(() => buildRows(allocation), [allocation]);
    const hasOverrides = rows.some((r) => r.override !== null);

    const heading = allocation.symbol || allocation.sId;
    const subheading = allocation.symbol ? allocation.description : undefined;
    const dialogTitle = allocation.symbol
        ? `${allocation.symbol}${allocation.description ? ` (${allocation.description})` : ''}`
        : allocation.sId;

    return (
        <>
            <div
                onClick={() => setManageOpen(true)}
                className="border-cocoa-100 hover:border-cocoa-200 hover:bg-cocoa-0 flex h-[100px] w-full cursor-pointer flex-col justify-between rounded-lg border bg-white p-3 shadow-sm transition-colors select-none"
            >
                <div className="min-w-0">
                    <div className="text-cocoa-1000 truncate text-sm font-semibold">{heading}</div>
                    {subheading && <div className="text-cocoa-600 truncate text-xs leading-snug">{subheading}</div>}
                </div>
                <div className="flex flex-wrap items-center justify-end gap-1">
                    {!!allocation.nrOfHoldings && (
                        <span className="text-cocoa-600 mr-auto text-[10px]">
                            {allocation.nrOfHoldings} {allocation.nrOfHoldings === 1 ? 'holding' : 'holdings'}
                        </span>
                    )}
                    {hasOverrides && (
                        <span className="text-accent bg-espresso-50 rounded-full px-2 py-0.5 text-[10px] font-semibold tracking-wide uppercase">
                            Override
                        </span>
                    )}
                    {uncategorized !== null && (
                        <span className="rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-semibold tracking-wide text-red-600 uppercase">
                            Uncategorized {formatRatio(uncategorized)}
                        </span>
                    )}
                </div>
            </div>

            <ManageOverridesDialog
                open={manageOpen}
                onOpenChange={setManageOpen}
                sId={allocation.sId}
                title={dialogTitle}
                rows={rows}
                hasOverrides={hasOverrides}
            />
        </>
    );
};
